import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { IComponentNode } from "../../types/pageBuilder.types";
import { usePageBuilder } from "../../context/PageBuilderContext";
import { 
 FaFont, 
 FaImage, 
 FaSquare, 
 FaTh, 
 FaBars, 
 FaVideo, 
 FaArrowsAltV,
 FaHeading,
 FaList,
 FaListOl,
 FaQuoteLeft,
 FaCode,
 FaLink,
 FaGripLines,
 FaShapes,
 FaIcons,
 FaDatabase
} from "react-icons/fa";

interface ComponentItem {
 type: string;
 label: string;
 icon: React.ReactNode;
 defaultProps: Record<string, any>;
 defaultStyles: any;
}

const componentTemplates: ComponentItem[] = [
 {
  type: "container",
  label: "Container",
  icon: <FaSquare />,
  defaultProps: {},
  defaultStyles: {
   default: {
    padding: "1rem",
    backgroundColor: "transparent",
   },
  },
 },
 {
  type: "heading",
  label: "Heading",
  icon: <FaHeading />,
  defaultProps: {
   text: "Heading Text",
   level: 1,
  },
  defaultStyles: {
   default: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#000000",
    marginBottom: "1rem",
   },
  },
 },
 {
  type: "text",
  label: "Text",
  icon: <FaFont />,
  defaultProps: {
   text: "Enter your text here",
  },
  defaultStyles: {
   default: {
    fontSize: "16px",
    color: "#000000",
   },
  },
 },
 {
  type: "image",
  label: "Image",
  icon: <FaImage />,
  defaultProps: {
   src: "",
   alt: "Image",
  },
  defaultStyles: {
   default: {
    width: "100%",
    maxWidth: "100%",
   },
  },
 },
 {
  type: "button",
  label: "Button",
  icon: <FaSquare />,
  defaultProps: {
   text: "Click me",
   href: "#",
  },
  defaultStyles: {
   default: {
    padding: "0.5rem 1rem",
    backgroundColor: "#4F46E5",
    color: "#ffffff",
    borderRadius: "0.25rem",
    cursor: "pointer",
    border: "none",
   },
  },
 },
 {
  type: "link",
  label: "Link",
  icon: <FaLink />,
  defaultProps: {
   text: "Link Text",
   href: "#",
  },
  defaultStyles: {
   default: {
    color: "#2563eb",
    textDecoration: "underline",
   },
  },
 },
 {
  type: "grid",
  label: "Grid",
  icon: <FaTh />,
  defaultProps: {
   columns: 3,
   rows: 1,
   gap: "1rem",
  },
  defaultStyles: {
   default: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "1rem",
   },
  },
 },
 {
  type: "list",
  label: "Unordered List",
  icon: <FaList />,
  defaultProps: {
   items: ["Item 1", "Item 2", "Item 3"],
  },
  defaultStyles: {
   default: {
    paddingLeft: "1.5rem",
   },
  },
 },
 {
  type: "ordered-list",
  label: "Ordered List",
  icon: <FaListOl />,
  defaultProps: {
   items: ["First item", "Second item", "Third item"],
  },
  defaultStyles: {
   default: {
    paddingLeft: "1.5rem",
   },
  },
 },
 {
  type: "quote",
  label: "Quote",
  icon: <FaQuoteLeft />,
  defaultProps: {
   text: "This is a quote",
   author: "",
  },
  defaultStyles: {
   default: {
    padding: "1rem",
    borderLeft: "4px solid #4F46E5",
    fontStyle: "italic",
    backgroundColor: "#f3f4f6",
   },
  },
 },
 {
  type: "divider",
  label: "Divider",
  icon: <FaGripLines />,
  defaultProps: {},
  defaultStyles: {
   default: {
    height: "1px",
    backgroundColor: "#e5e7eb",
    margin: "2rem 0",
   },
  },
 },
 {
  type: "carousel",
  label: "Carousel",
  icon: <FaBars />,
  defaultProps: {
   autoplay: false,
   interval: 3000,
  },
  defaultStyles: {
   default: {
    display: "flex",
    overflowX: "auto",
    gap: "1rem",
   },
  },
 },
 {
  type: "spacer",
  label: "Spacer",
  icon: <FaArrowsAltV />,
  defaultProps: {
   height: "2rem",
  },
  defaultStyles: {
   default: {
    height: "2rem",
   },
  },
 },
 {
  type: "video",
  label: "Video",
  icon: <FaVideo />,
  defaultProps: {
   src: "",
   autoplay: false,
   controls: true,
  },
  defaultStyles: {
   default: {
    width: "100%",
   },
  },
 },
 {
  type: "code",
  label: "Code Block",
  icon: <FaCode />,
  defaultProps: {
   code: "// Your code here",
   language: "javascript",
  },
  defaultStyles: {
   default: {
    padding: "1rem",
    backgroundColor: "#1f2937",
    color: "#f9fafb",
    borderRadius: "0.25rem",
    fontFamily: "monospace",
   },
  },
 },
 {
  type: "card",
  label: "Card",
  icon: <FaShapes />,
  defaultProps: {
   title: "Card Title",
   content: "Card content goes here",
  },
  defaultStyles: {
   default: {
    padding: "1.5rem",
    backgroundColor: "#ffffff",
    borderRadius: "0.5rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
   },
  },
 },
 {
  type: "icon",
  label: "Icon",
  icon: <FaIcons />,
  defaultProps: {
   iconName: "FaCheck",
   iconLibrary: "fa",
   color: "#4F46E5",
   size: "24",
  },
  defaultStyles: {
   default: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
   },
  },
 },
 {
  type: "dynamic-snippet",
  label: "Dynamic Snippet",
  icon: <FaDatabase />,
  defaultProps: {
   placeholder: "Configure dynamic content",
  },
  defaultStyles: {
   default: {
    padding: "2rem",
    border: "2px dashed #d1d5db",
    borderRadius: "0.5rem",
    backgroundColor: "#f9fafb",
    minHeight: "200px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
   },
  },
 },
];

export { componentTemplates };

const DraggableComponentItem: React.FC<{ template: ComponentItem; onAdd: () => void }> = ({ template, onAdd }) => {
 const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
  id: `palette-${template.type}`,
  data: { type: "palette-item", template },
 });

 const style = transform
  ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
   }
  : undefined;

 return (
  <button
   type="button"
   ref={setNodeRef}
   style={style}
   {...listeners}
   {...attributes}
   onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    onAdd();
   }}
   onMouseDown={(e) => {
    e.stopPropagation();
   }}
   onMouseUp={(e) => {
    e.stopPropagation();
   }}
   className={`w-full flex items-center gap-3 px-3 py-2 text-left border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 cursor-grab active:cursor-grabbing ${
    isDragging ? "opacity-50" : ""
   }`}
  >
   <span className="text-gray-600">{template.icon}</span>
   <span className="text-sm font-medium">{template.label}</span>
  </button>
 );
};

export const ComponentPalette: React.FC = () => {
 const { layout, addComponent, selectedComponent, selectedDropTarget, setSelectedDropTarget } = usePageBuilder();

 const handleAddComponent = (template: ComponentItem) => {
  const newComponent: IComponentNode = {
   id: `${template.type}-${Date.now()}`,
   type: template.type,
   props: { ...template.defaultProps },
   styles: template.defaultStyles,
   children: [],
  };

  // If a drop target is selected, add to it
  // Otherwise, if a component is selected and it can have children, add to it
  // Otherwise, add to root
  let parentId: string | null = null;
  if (selectedDropTarget) {
   parentId = selectedDropTarget;
   // Clear the selected drop target after adding
   setSelectedDropTarget(null);
  } else if (selectedComponent && (selectedComponent.type === "container" || selectedComponent.type === "grid" || selectedComponent.type === "carousel")) {
   parentId = selectedComponent.id;
  } else {
   parentId = layout?.id || null;
  }
  
  addComponent(parentId, newComponent);
 };

 return (
  <div className="p-4">
   <h2 className="text-lg font-semibold mb-4">Components</h2>
   <div className="space-y-2">
    {componentTemplates.map((template) => (
     <DraggableComponentItem
      key={template.type}
      template={template}
      onAdd={() => handleAddComponent(template)}
     />
    ))}
   </div>

  </div>
 );
};

